const curtainIntroSelector = '[data-printale-curtain-intro]';
const sessionKey = 'printaleCurtainIntroSeen';
const openingDuration = 3200;
const fadeDuration = 1250;
const screenRevealLeadTime = 820;
const threeModuleUrl = 'three'; // resolved via importmap → local three.module.js

function createCurtainTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 1024;

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const base = ctx.createLinearGradient(0, 0, canvas.width, 0);
  base.addColorStop(0, '#3d030b');
  base.addColorStop(0.12, '#7f1323');
  base.addColorStop(0.24, '#e14b61');
  base.addColorStop(0.38, '#6c0b18');
  base.addColorStop(0.52, '#b9263b');
  base.addColorStop(0.68, '#f06573');
  base.addColorStop(0.82, '#7b1020');
  base.addColorStop(1, '#2d0208');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  for (let x = 0; x < canvas.width; x += 34) {
    const fold = ctx.createLinearGradient(x, 0, x + 34, 0);
    fold.addColorStop(0, 'rgba(0,0,0,0.22)');
    fold.addColorStop(0.34, 'rgba(255,225,210,0.18)');
    fold.addColorStop(0.52, 'rgba(255,255,255,0.06)');
    fold.addColorStop(1, 'rgba(0,0,0,0.24)');
    ctx.fillStyle = fold;
    ctx.fillRect(x, 0, 34, canvas.height);
  }

  const topGlow = ctx.createRadialGradient(256, 0, 0, 256, 0, 520);
  topGlow.addColorStop(0, 'rgba(255,220,185,0.42)');
  topGlow.addColorStop(1, 'rgba(255,220,185,0)');
  ctx.fillStyle = topGlow;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const bottomShadow = ctx.createLinearGradient(0, 640, 0, canvas.height);
  bottomShadow.addColorStop(0, 'rgba(0,0,0,0)');
  bottomShadow.addColorStop(1, 'rgba(0,0,0,0.28)');
  ctx.fillStyle = bottomShadow;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Subtle grain — reduced from 0.18 to avoid the worn/hazy look
  ctx.globalAlpha = 0.055;
  for (let i = 0; i < 1200; i += 1) {
    const x = Math.random() * canvas.width;
    const y = Math.random() * canvas.height;
    const tone = Math.random() > 0.5 ? 255 : 40;
    ctx.fillStyle = `rgb(${tone}, ${tone}, ${tone})`;
    ctx.fillRect(x, y, 1, 1);
  }
  ctx.globalAlpha = 1;

  return canvas;
}

function createValanceTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 256;

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const base = ctx.createLinearGradient(0, 0, 0, canvas.height);
  base.addColorStop(0, '#f36a76');
  base.addColorStop(0.48, '#b9273d');
  base.addColorStop(1, '#46050e');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  for (let x = 0; x < canvas.width; x += 68) {
    const fold = ctx.createLinearGradient(x, 0, x + 68, 0);
    fold.addColorStop(0, 'rgba(0,0,0,0.35)');
    fold.addColorStop(0.42, 'rgba(255,235,210,0.24)');
    fold.addColorStop(1, 'rgba(0,0,0,0.38)');
    ctx.fillStyle = fold;
    ctx.fillRect(x, 0, 68, canvas.height);
  }

  ctx.fillStyle = 'rgba(255,235,206,0.18)';
  ctx.fillRect(0, 0, canvas.width, 28);
  ctx.fillStyle = 'rgba(0,0,0,0.28)';
  ctx.fillRect(0, canvas.height - 44, canvas.width, 44);

  return canvas;
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function makeCurtainMesh(THREE, side, texture) {
  const group = new THREE.Group();
  const strips = 16;
  const stripWidth = 0.47;
  const height = 5.6;
  const direction = side === 'left' ? -1 : 1;

  for (let i = 0; i < strips; i += 1) {
    const geometry = new THREE.PlaneGeometry(stripWidth, height, 8, 32);
    const position = geometry.attributes.position;

    for (let point = 0; point < position.count; point += 1) {
      const x = position.getX(point);
      const y = position.getY(point);
      const wave = Math.sin((x / stripWidth) * Math.PI * 2 + i * 0.72) * 0.08;
      const drape = Math.pow((height / 2 - y) / height, 2) * 0.18;
      position.setZ(point, wave + drape);
      position.setY(point, y - drape * 0.22);
    }

    geometry.computeVertexNormals();

    const material = new THREE.MeshStandardMaterial({
      map: texture,
      color: new THREE.Color(i % 2 === 0 ? '#cf3a50' : '#9e1b33'),
      roughness: 0.72,
      metalness: 0.03,
      side: THREE.DoubleSide,
    });

    const strip = new THREE.Mesh(geometry, material);
    strip.position.x = direction * (1.9 + i * stripWidth * 0.82);
    strip.position.y = -0.25;
    strip.position.z = (i % 2 === 0 ? 0.18 : -0.08) + i * 0.004;
    strip.rotation.y = direction * (i % 2 === 0 ? -0.22 : 0.16);
    strip.userData = {
      baseX: strip.position.x,
      baseZ: strip.position.z,
      baseRotationY: strip.rotation.y,
      delay: i * 0.012,
      direction,
    };

    group.add(strip);
  }

  return group;
}

function makeValanceMesh(THREE, texture) {
  const group = new THREE.Group();
  const valanceWidth = 13.8;
  const scallopCount = 13;
  const scallopSpacing = valanceWidth / scallopCount;
  const material = new THREE.MeshStandardMaterial({
    map: texture,
    color: '#d13c52',
    roughness: 0.76,
    metalness: 0.04,
    side: THREE.DoubleSide,
  });

  const main = new THREE.Mesh(new THREE.BoxGeometry(valanceWidth, 0.82, 0.42, 48, 4, 2), material);
  main.position.y = 2.88;
  main.position.z = 0.34;
  main.userData.baseY = main.position.y;
  group.add(main);

  for (let i = 0; i < scallopCount; i += 1) {
    const scallop = new THREE.Mesh(new THREE.SphereGeometry(0.58, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2), material);
    scallop.position.x = (-valanceWidth / 2) + (scallopSpacing / 2) + i * scallopSpacing;
    scallop.position.y = 2.48;
    scallop.position.z = 0.38;
    scallop.scale.set(1.08, 0.72, 0.44);
    scallop.rotation.x = Math.PI;
    scallop.userData.baseY = scallop.position.y;
    group.add(scallop);
  }

  return group;
}

async function initThreeCurtain(intro) {
  if (prefersReducedMotion()) return null;

  const THREE = await import(threeModuleUrl);
  const canvas = document.createElement('canvas');
  canvas.className = 'printale-curtain-webgl';
  canvas.setAttribute('aria-hidden', 'true');
  intro.prepend(canvas);

  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(36, 1, 0.1, 100);
  camera.position.set(0, 0.35, 9.4);
  camera.lookAt(0, 0, 0);

  const curtainTexture = new THREE.CanvasTexture(createCurtainTexture());
  curtainTexture.colorSpace = THREE.SRGBColorSpace;
  curtainTexture.wrapS = THREE.RepeatWrapping;
  curtainTexture.wrapT = THREE.RepeatWrapping;
  curtainTexture.repeat.set(1, 1);

  const valanceTexture = new THREE.CanvasTexture(createValanceTexture());
  valanceTexture.colorSpace = THREE.SRGBColorSpace;
  valanceTexture.wrapS = THREE.RepeatWrapping;
  valanceTexture.wrapT = THREE.RepeatWrapping;

  const leftCurtain = makeCurtainMesh(THREE, 'left', curtainTexture);
  const rightCurtain = makeCurtainMesh(THREE, 'right', curtainTexture);
  const valance = makeValanceMesh(THREE, valanceTexture);
  const root = new THREE.Group();
  root.add(leftCurtain, rightCurtain, valance);
  scene.add(root);

  const backWall = new THREE.Mesh(
    new THREE.PlaneGeometry(12, 7),
    new THREE.MeshStandardMaterial({
      color: '#4e1016',
      roughness: 0.88,
      metalness: 0,
    })
  );
  backWall.position.z = -0.9;
  scene.add(backWall);

  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(12, 5, 24, 6),
    new THREE.MeshStandardMaterial({
      color: '#8f211f',
      roughness: 0.8,
      metalness: 0.02,
    })
  );
  floor.position.y = -3.12;
  floor.position.z = 1.05;
  floor.rotation.x = -Math.PI / 2.35;
  floor.receiveShadow = true;
  scene.add(floor);

  const ambient = new THREE.AmbientLight('#ffc3b1', 1.8);
  scene.add(ambient);

  const keyLight = new THREE.SpotLight('#ffe2b4', 80, 15, Math.PI / 5.2, 0.58, 1);
  keyLight.position.set(0, 4.5, 6.5);
  keyLight.target.position.set(0, -1, -0.4);
  keyLight.castShadow = true;
  scene.add(keyLight, keyLight.target);

  const leftLight = new THREE.PointLight('#ff7890', 14, 10);
  leftLight.position.set(-4.2, 1.4, 3.2);
  const rightLight = new THREE.PointLight('#ffb46c', 12, 10);
  rightLight.position.set(4.2, 1.3, 3.2);
  scene.add(leftLight, rightLight);

  let progress = 0;
  let openingStartTime = null;
  let rafId = 0;
  let disposed = false;

  function resize() {
    const { clientWidth, clientHeight } = intro;
    renderer.setSize(clientWidth, clientHeight, false);
    camera.aspect = clientWidth / Math.max(clientHeight, 1);
    camera.updateProjectionMatrix();

    const visibleHeight = 2 * Math.tan((camera.fov * Math.PI / 180) / 2) * camera.position.z;
    const coverScale = visibleHeight / 5.25;
    const rootScale = Math.max(clientWidth < 750 ? 1.08 : 1, coverScale);
    root.scale.setScalar(rootScale);
    root.position.y = clientWidth < 750 ? -0.16 : 0;
  }

  function updateCurtainGroup(group, sideProgress) {
    group.children.forEach((strip) => {
      const eased = easeInOutCubic(Math.max(0, Math.min(1, sideProgress - strip.userData.delay)));
      const direction = strip.userData.direction;
      strip.position.x = strip.userData.baseX + direction * eased * (3.2 + Math.abs(strip.userData.baseX) * 0.24);
      strip.position.z = strip.userData.baseZ + eased * 1.55;
      strip.position.y = -0.25 + Math.sin(eased * Math.PI) * 0.12;
      strip.rotation.y = strip.userData.baseRotationY + direction * eased * -0.72;
      strip.rotation.z = direction * eased * 0.08;
      strip.scale.x = 1 - eased * 0.32;
      strip.scale.y = 1 + Math.sin(eased * Math.PI) * 0.035;
    });
  }

  function draw(now = performance.now()) {
    if (disposed) return;

    if (openingStartTime !== null) {
      const rawProgress = Math.min(1, (now - openingStartTime) / openingDuration);
      progress = easeInOutCubic(rawProgress);
    }

    updateCurtainGroup(leftCurtain, progress);
    updateCurtainGroup(rightCurtain, progress);

    const valanceProgress = easeInOutCubic(Math.min(1, Math.max(0, progress * 1.35 - 0.12)));
    const connectedBob = Math.sin(now / 620) * 0.018 * (1 - valanceProgress);
    valance.position.y = valanceProgress * 3.25 + connectedBob;
    valance.rotation.x = -valanceProgress * 0.48;
    valance.scale.y = 1 - valanceProgress * 0.38;
    valance.scale.x = 1 + valanceProgress * 0.05;

    keyLight.intensity = 80 + progress * 32;
    backWall.material.color.set(progress > 0.48 ? '#6c1919' : '#4e1016');
    renderer.render(scene, camera);
    rafId = window.requestAnimationFrame(draw);
  }

  resize();
  window.addEventListener('resize', resize);
  intro.classList.add('is-three-ready');
  draw();

  return {
    open() {
      if (openingStartTime === null) {
        openingStartTime = performance.now();
      }
    },
    dispose() {
      disposed = true;
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      renderer.dispose();
      curtainTexture.dispose();
      valanceTexture.dispose();
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) object.material.dispose();
      });
    },
  };
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Uses sessionStorage — clears when the tab/browser closes.
// This means: new tab = always shows intro. Same-tab refresh = skips.
// "Replay Intro" button clears the key and reloads so it shows immediately.
function canUseStorage() {
  try {
    sessionStorage.setItem('printaleCurtainTest', '1');
    sessionStorage.removeItem('printaleCurtainTest');
    return true;
  } catch (error) {
    return false;
  }
}

function hasSeenIntro(storageAvailable) {
  if (!storageAvailable) return false;
  return sessionStorage.getItem(sessionKey) === '1';
}

function markIntroSeen(storageAvailable) {
  if (!storageAvailable) return;
  sessionStorage.setItem(sessionKey, '1');
}

// Allow the "Replay Intro" footer button to force replay
function clearIntroSeen() {
  try { sessionStorage.removeItem(sessionKey); } catch {}
}

function removeIntro(intro) {
  if (intro.printaleCurtainThree?.dispose) {
    intro.printaleCurtainThree.dispose();
  }

  intro.classList.add('is-gone');
  intro.setAttribute('hidden', '');
  document.body.classList.remove('printale-curtain-lock');
  document.body.classList.remove('printale-curtain-revealing', 'printale-curtain-screen-live', 'printale-curtain-opening');

  const flare = document.querySelector('.printale-curtain-screen-flare');
  if (flare) {
    flare.remove();
  }
}

function startScreenReveal() {
  document.body.classList.remove('printale-curtain-lock');
  document.body.classList.add('printale-curtain-screen-live');
}

function openIntro(intro, storageAvailable) {
  if (intro.dataset.opening === 'true') return;

  intro.dataset.opening = 'true';
  document.body.classList.add('printale-curtain-revealing');
  document.body.classList.add('printale-curtain-opening');

  const flare = document.createElement('div');
  flare.className = 'printale-curtain-screen-flare';
  flare.setAttribute('aria-hidden', 'true');
  document.body.append(flare);

  if (prefersReducedMotion()) {
    removeIntro(intro);
    return;
  }

  if (intro.printaleCurtainThree?.open) {
    intro.printaleCurtainThree.open();
  }

  intro.classList.add('is-opening');

  window.setTimeout(() => {
    startScreenReveal();
  }, Math.max(0, openingDuration - screenRevealLeadTime));

  window.setTimeout(() => {
    intro.classList.add('is-fading');

    window.setTimeout(() => {
      removeIntro(intro);
    }, fadeDuration);
  }, openingDuration);
}

function initCurtainIntro(intro) {
  const storageAvailable = canUseStorage();
  const forceReplay = new URLSearchParams(window.location.search).get('curtain') === 'replay';

  if (!forceReplay && hasSeenIntro(storageAvailable)) {
    removeIntro(intro);
    return;
  }

  // Mark as seen immediately so a same-tab refresh doesn't re-show it
  markIntroSeen(storageAvailable);

  document.body.classList.add('printale-curtain-lock');
  document.body.classList.add('printale-curtain-revealing');
  intro.removeAttribute('hidden');

  initThreeCurtain(intro)
    .then((threeCurtain) => {
      if (threeCurtain) {
        intro.printaleCurtainThree = threeCurtain;
      }
    })
    .catch(() => {
      intro.classList.remove('is-three-ready');
    });

  const openButtons = intro.querySelectorAll('[data-printale-curtain-open]');
  openButtons.forEach((button) => {
    button.addEventListener('click', () => openIntro(intro, storageAvailable));
  });

  intro.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      openIntro(intro, storageAvailable);
    }
  });

  const primaryButton = intro.querySelector('[data-printale-curtain-open]');
  if (primaryButton instanceof HTMLElement) {
    window.requestAnimationFrame(() => primaryButton.focus({ preventScroll: true }));
  }
}

document.querySelectorAll(curtainIntroSelector).forEach(initCurtainIntro);

// "Replay Intro" footer button — clear TTL and reload
const replayBtn = document.getElementById('printale-replay-intro');
if (replayBtn) {
  replayBtn.addEventListener('click', () => {
    clearIntroSeen();
    window.location.reload();
  });
}
